import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { log, error } from '@/services/Log'
import type {
  StandardAddress,
  StandardCart,
  StandardOrder,
  StandardShipment,
} from '@/types/ShopFront/CheckoutStandards'

const shipmentCrashedWith = (AddressValidator: (addr: StandardAddress) => boolean) => ({
  availableShippingOptions, shippingAddress,
}: StandardShipment) => (
  !availableShippingOptions.length
  && !!AddressValidator(shippingAddress)
)
const getOrderInitializer = ({
  isOrderUpdatePending,
  isValidAddress,
  recreateOrder,
  createNonBopusConsignments,
  generateEmptyStandardAddress,
}: {
  isOrderUpdatePending: Awaited<typeof import('@/services/FramedCheckout/shipmentServices/updateShippingAddress')>['isOrderUpdatePending']
  isValidAddress: Awaited<typeof import('@/helpers/addressHelpers')>['isValidAddress']
  recreateOrder: Awaited<typeof import('@/services/FramedCheckout/orderServices/recreateOrder')>['recreateOrder']
  createNonBopusConsignments: Awaited<typeof import('@/services/FramedCheckout/shipmentServices/createNonBopusConsignments')>['createNonBopusConsignments']
  generateEmptyStandardAddress: Awaited<typeof import('@/services/Standardizers/standardizeAddressSavedInstrumentsApi')>['generateEmptyStandardAddress']
}) => async ({ order }: { order: StandardOrder }) : Promise<StandardOrder> => {
  let newOrder: StandardOrder = { ...order }
  const bigcommerceHasFailedShippingOptionsCache = (
    !!order.shipments.find(shipmentCrashedWith(isValidAddress))
  )
  if (bigcommerceHasFailedShippingOptionsCache) {
    log('bigcommerce Has Failed Shipping Options Cache')
    const recoveredOrder = await recreateOrder({ order: newOrder })
    if (recoveredOrder.success && recoveredOrder.newOrder) {
      newOrder = recoveredOrder.newOrder
    }
  }

  const usedAddress = newOrder?.shipments?.find?.(({ isBopus }) => !isBopus)?.shippingAddress
  const isUpdatPending = (
    usedAddress && await isOrderUpdatePending({ order: newOrder, address: usedAddress })
  )
  const shouldSplitFurnitureShipment = !!newOrder.shipments.find(({ isBopus, lineItems }) => (
    !isBopus
    && !!lineItems.find(({ isFurniture }) => isFurniture)
    && !!lineItems.find(({ isFurniture }) => !isFurniture)
  )) && usedAddress
  const hasUnassignedLineItems = !!newOrder.cart.lineItems.find(({ id }) => (
    !newOrder.shipments.find(({ lineItemIds }) => lineItemIds.includes(id))
  ))
  if (shouldSplitFurnitureShipment || isUpdatPending || hasUnassignedLineItems) {
    const address = usedAddress || generateEmptyStandardAddress()
    const response = await createNonBopusConsignments({
      order: newOrder, address,
    })
    newOrder = response.newOrder
    log('Split Furniture Shipment', { newOrder })
  }
  // Should check for multiple BOPUS shipments for the same store
  return newOrder
}

export const initializeCheckout = async (): Promise<{
  success: true,
  cart: StandardCart,
  order: StandardOrder,
  customer: unknown,
  addresses: StandardAddress[],
  savedInstruments: unknown[],
} | {
  success: false
  cart?: undefined,
  order?: undefined,
  customer?: undefined,
  addresses?: undefined,
  savedInstruments?: undefined,
}> => {
  log('initializeCheckout Start')
  const [
    { default: getOrder },
    { getCart },
    { getCustomer },
    { isOrderUpdatePending },
    { isValidAddress },
    { recreateOrder },
    { createNonBopusConsignments },
    { getPaymentMethods },
    { trackCheckoutOnFacebookPixel },
    { generateEmptyStandardAddress },
  ] = await allPromisesWithRetries(() => [
    import('@/services/FramedCheckout/orderServices/getOrder'),
    import('@/services/FramedCheckout/orderServices/getCart'),
    import('@/services/FramedCheckout/customerServices'),
    import('@/services/FramedCheckout/shipmentServices/updateShippingAddress'),
    import('@/helpers/addressHelpers'),
    import('@/services/FramedCheckout/orderServices/recreateOrder'),
    import('@/services/FramedCheckout/shipmentServices/createNonBopusConsignments'),
    import('@/services/FramedCheckout/paymentServices/getPaymentMethods'),
    import('@/services/FramedCheckout/orderServices/trackCheckoutOnFacebookPixel'),
    import('@/services/Standardizers/standardizeAddressSavedInstrumentsApi'),
  ])

  try {
    const cart = await getCart()
    if (!cart) {
      const { goToCart } = await import('@/services/FramedCheckout/navigationServices')
      await goToCart()
      return { success: false }
    }
    const initialOrder = await getOrder({ cart })
    const initializeOrder = getOrderInitializer({
      isOrderUpdatePending,
      isValidAddress,
      recreateOrder,
      createNonBopusConsignments,
      generateEmptyStandardAddress,
    })
    const order = await initializeOrder({ order: initialOrder })
    const customer = await getCustomer()
    const addresses = (customer?.addresses || [])
    const savedInstruments = customer ? await getPaymentMethods() : []

    log('initializeCheckout End', {
      success: true, cart, order, customer, addresses, savedInstruments,
    })

    trackCheckoutOnFacebookPixel({ customer, cart, order })

    return {
      success: true, cart, order, customer, addresses, savedInstruments,
    }
  } catch (err) {
    error(`initializeCheckout FAILED: ${String(err)}`, err)
    return initializeCheckout()
  }
}

export default initializeCheckout
